/**
 * North Point Symbol Library
 * 
 * Pure SVG path data for compass/north point symbols.
 * No hardcoded fills - use currentColor for theme control.
 * All symbols designed for 100x100 viewBox.
 */

export type NorthPointStyle = 
  | 'compass-rose'
  | 'n-arrow'
  | 'hollow-arrow'
  | 'circle-arrow'
  | 'triangle'
  | 'double-line';

export interface NorthPointSymbol {
  id: NorthPointStyle;
  name: string;
  viewBox: string;
  paths: {
    d: string;
    fill?: 'currentColor' | 'none';
    stroke?: 'currentColor';
    strokeWidth?: number;
  }[];
}

/**
 * North Point Symbol Definitions
 * All symbols are designed for a 100x100 viewBox with center at (50, 50)
 */
export const NORTH_POINT_SYMBOLS: NorthPointSymbol[] = [
  {
    id: 'compass-rose',
    name: 'Classic Compass Rose',
    viewBox: '0 0 100 100',
    paths: [
      // Outer circle
      {
        d: 'M50 5 A45 45 0 1 1 50 95 A45 45 0 1 1 50 5',
        fill: 'none',
        stroke: 'currentColor',
        strokeWidth: 2
      },
      // North arrow (filled)
      {
        d: 'M50 10 L58 45 L50 40 L42 45 Z',
        fill: 'currentColor'
      },
      // South arrow (outline)
      {
        d: 'M50 90 L58 55 L50 60 L42 55 Z',
        fill: 'none',
        stroke: 'currentColor',
        strokeWidth: 1.5
      },
      // East arrow
      {
        d: 'M90 50 L55 42 L60 50 L55 58 Z',
        fill: 'none',
        stroke: 'currentColor',
        strokeWidth: 1.5
      },
      // West arrow
      {
        d: 'M10 50 L45 42 L40 50 L45 58 Z',
        fill: 'none',
        stroke: 'currentColor',
        strokeWidth: 1.5
      },
      // Center circle
      {
        d: 'M50 45 A5 5 0 1 1 50 55 A5 5 0 1 1 50 45',
        fill: 'currentColor'
      }
    ]
  },
  {
    id: 'n-arrow',
    name: 'Simple N Arrow',
    viewBox: '0 0 100 100',
    paths: [
      // Arrow body
      {
        d: 'M50 5 L65 35 L55 35 L55 95 L45 95 L45 35 L35 35 Z',
        fill: 'currentColor'
      },
      // N letter (cut out effect via stroke)
      {
        d: 'M42 50 L42 70 L50 60 L58 70 L58 50',
        fill: 'none',
        stroke: 'currentColor',
        strokeWidth: 0
      }
    ]
  },
  {
    id: 'hollow-arrow',
    name: 'Hollow Arrow',
    viewBox: '0 0 100 100',
    paths: [
      // Outer arrow shape
      {
        d: 'M50 5 L75 40 L60 40 L60 95 L40 95 L40 40 L25 40 Z',
        fill: 'none',
        stroke: 'currentColor',
        strokeWidth: 3
      },
      // N letter inside
      {
        d: 'M44 55 L44 80 L50 70 L56 80 L56 55',
        fill: 'none',
        stroke: 'currentColor',
        strokeWidth: 2
      }
    ]
  },
  {
    id: 'circle-arrow',
    name: 'Circle + Arrow',
    viewBox: '0 0 100 100',
    paths: [
      // Outer circle
      {
        d: 'M50 10 A40 40 0 1 1 50 90 A40 40 0 1 1 50 10',
        fill: 'none',
        stroke: 'currentColor',
        strokeWidth: 2
      },
      // Arrow pointing up
      {
        d: 'M50 20 L62 45 L54 45 L54 70 L46 70 L46 45 L38 45 Z',
        fill: 'currentColor'
      },
      // N letter below arrow
      {
        d: 'M42 75 L42 88 L50 82 L58 88 L58 75',
        fill: 'none',
        stroke: 'currentColor',
        strokeWidth: 2
      }
    ]
  },
  {
    id: 'triangle',
    name: 'Minimal Triangle',
    viewBox: '0 0 100 100',
    paths: [
      // Simple triangle arrow
      {
        d: 'M50 5 L80 90 L50 70 L20 90 Z',
        fill: 'currentColor'
      }
    ]
  },
  {
    id: 'double-line',
    name: 'Double-Line Arrow',
    viewBox: '0 0 100 100',
    paths: [
      // Left line of arrow
      {
        d: 'M45 5 L45 95',
        fill: 'none',
        stroke: 'currentColor',
        strokeWidth: 3
      },
      // Right line of arrow
      {
        d: 'M55 5 L55 95',
        fill: 'none',
        stroke: 'currentColor',
        strokeWidth: 3
      },
      // Arrow head
      {
        d: 'M50 5 L70 30 L55 30 M50 5 L30 30 L45 30',
        fill: 'none',
        stroke: 'currentColor',
        strokeWidth: 3
      },
      // N crossbar
      {
        d: 'M45 50 L55 65 M45 65 L55 50',
        fill: 'none',
        stroke: 'currentColor',
        strokeWidth: 2
      }
    ]
  }
];

/**
 * Get a north point symbol by its style ID
 */
export function getNorthPointSymbol(style: NorthPointStyle): NorthPointSymbol | undefined {
  return NORTH_POINT_SYMBOLS.find(s => s.id === style);
}

/**
 * Convert a north point symbol to an SVG string
 */
export function northPointToSvg(
  symbol: NorthPointSymbol, 
  color: string = '#000000',
  size: number = 100
): string {
  const paths = symbol.paths.map(path => {
    const fill = path.fill === 'currentColor' ? color : (path.fill || 'none');
    const stroke = path.stroke === 'currentColor' ? color : (path.stroke || 'none');
    const strokeWidth = path.strokeWidth || 0;
    
    return `<path d="${path.d}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}" />`;
  }).join('\n    ');

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${symbol.viewBox}" width="${size}" height="${size}">
    ${paths}
  </svg>`;
}

/**
 * Default size for north point symbols (in pixels)
 */
export const DEFAULT_NORTH_POINT_SIZE = 60;

/**
 * Default position (bottom-right corner, normalized 0-1)
 */
export const DEFAULT_NORTH_POINT_POSITION = { x: 0.9, y: 0.9 };
